<template>
  <div v-if="!resumed">
    <v-skeleton-loader type="paragraph" />
  </div>
  <div v-else>
    <v-toolbar
      class="rounded-toolbar mb-6 white--text overflow-hidden"
      color="#e06f18"
      dense
    >
      <v-row class="fill-height" align="center">
        <v-col cols="auto" class="px-2 py-0">
          <v-select
            v-model="selectedOrderType"
            class="no-bottom-border all-white mt-4 white--text hide-placeholder"
            dark
            :items="resumedOrderTypes"
            hide-details
            :menu-props="{ offsetY: true }"
            color="#ffffff"
            label="Ordenar por"
            item-value="id"
            return-object
            flat
            dense
            item-text="name"
          ></v-select>
        </v-col>
        <v-col cols="auto" class="px-2 py-0">
          <v-text-field
            hide-details
            filled
            dark
            dense
            prepend-inner-icon="mdi-magnify"
            clearable
            placeholder="Nombre o ID"
            class="mt-0 no-bottom-border all-white white--text hide-placeholder"
            v-model="resumedFilter"
          ></v-text-field>
        </v-col>
        <v-col cols="auto" class="px-2 py-0">
          <v-checkbox
            label="Ocultar Lite"
            dark
            dense
            hide-details
            v-model="filters.hideLite"
          />
        </v-col>
        <v-col cols="auto" class="px-2 py-0">
          <v-checkbox
            label="Solo tiendas que han pagado"
            hide-details
            dark
            dense
            v-model="filters.onlyPaid"
          />
        </v-col>
        <v-col cols="auto" class="px-2 py-0">
          <v-checkbox
            label="Solo c/ pedidos última semana"
            hide-details
            dark
            dense
            v-model="filters.onlyHaveOrders"
          />
        </v-col>
      </v-row>
    </v-toolbar>
    <div style="position: relative">
      <v-overlay :value="loadingResumed" :absolute="'absolute'">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>

      <v-data-table
        :items="resumed.items"
        :headers="headers"
        disable-sort
        disable-pagination
        dense
        hide-default-footer
        class="v-data-table--striped"
        no-data-text="No hay tiendas"
        no-results-text="No hay tiendas"
      >
        <template v-slot:item.picture="{ item }">
          <v-img
            class="logo mr-2"
            :max-width="imgSize"
            :max-height="imgSize"
            :width="imgSize"
            :height="imgSize"
            style="border-radius: 8px; overflow: hidden"
            :src="
              item.logo
                ? `${uploadsPath}/${item.logo}`
                : `assets/img/restaurant-logo.png`
            "
          ></v-img>
        </template>

        <template v-slot:item.id="{ item }">
          <div class="d-flex flex-column col-auto py-0">
            <div class="children-inline">
              <h3>{{ item.name }}</h3>
              <span class="font-size-9e grey--text">{{ item.stringId }}</span>
              <div class="chips--no-height">
                <v-chip
                  v-if="isPlanExpired(item.planExpiresAt)"
                  label
                  x-small
                  color="red"
                  text-color="white"
                  >Vencido</v-chip
                >
                <v-chip
                  v-else-if="getEffectiveTier(item) === 0"
                  label
                  x-small
                  color="green"
                  text-color="white"
                  >Lite</v-chip
                >
                <v-chip
                  v-else-if="getEffectiveTier(item) === 1"
                  label
                  x-small
                  color="orange"
                  text-color="white"
                  >Plus</v-chip
                >
                <v-chip
                  v-else
                  label
                  x-small
                  color="#ff6f00"
                  text-color="white"
                  >Pro</v-chip
                >
              </div>
              <v-btn
                icon
                x-small
                :href="`https://${domain}/${item.stringId}`"
                target="_blank"
                ><v-icon>mdi-link-variant</v-icon></v-btn
              >
            </div>
            <div v-if="item.planExpiresAt">
              <span class="font-size-9e grey--text"
                >Vence
                <timeago
                  :datetime="item.planExpiresAt"
                  :auto-update="60"
                ></timeago>
                |
              </span>
              <span class="font-size-9e grey--text"
                >{{ item.ordersLastWeek }} pedidos última semana</span
              >
            </div>
          </div>
        </template>

        <template v-slot:item.contact="{ item }">
          <v-btn
            v-if="item.userWhatsapp"
            color="green"
            dark
            rounded
            small
            class="px-2"
            style="padding-top: 2px"
            target="_blank"
            v-tooltip.bottom="'Whatsapp del usuario'"
            :href="`https://wa.me/${item.userWhatsapp.replace(
              '+',
              ''
            )}?text=${getWhatsappMessage(item)}`"
          >
            <v-icon small>mdi-account</v-icon>
            <v-icon>mdi-whatsapp</v-icon>
          </v-btn>
          <v-btn
            v-if="item.ordersWhatsapp || item.whatsapp"
            color="green"
            dark
            rounded
            small
            class="px-2"
            style="padding-top: 2px"
            target="_blank"
            v-tooltip.bottom="'Whatsapp de la tienda'"
            :href="`https://wa.me/${(
              item.ordersWhatsapp || item.whatsapp
            ).replace('+', '')}?text=${getWhatsappMessage(item)}`"
          >
            <v-icon small>mdi-store</v-icon>
            <v-icon>mdi-whatsapp</v-icon>
          </v-btn>
        </template>

        <template v-slot:item.createdAt="{ item }">
          <timeago :datetime="item.createdAt"></timeago>
        </template>

        <template v-slot:item.lastPaymentDate="{ item }">
          <div
            v-if="item.lastPaymentDate"
            class="tw-flex tw-items-center tw-space-x-1"
          >
            <timeago :datetime="item.lastPaymentDate"></timeago>
            <span class="grey--text font-size-9e"
              >{{ item.lastPaymentCurrency }}{{ item.lastPaymentAmount }}</span
            >
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="tw-flex tw-items-center tw-space-x-1">
            <v-btn
              color="primary"
              small
              depressed
              @click="showAddPlanDialog(item)"
              >+ plan</v-btn
            >
            <v-btn color="info" small depressed @click="showUploadMenu(item)"
              >Cargar menú</v-btn
            >
          </div>
        </template>
      </v-data-table>
      <!--<div class="d-flex justify-space-between align-center mb-2"
           v-for="r of resumed.items"
      >


        <div class="children-inline col-auto py-0">

        </div>
        <v-spacer />
      </div>-->
    </div>
    <v-pagination
      v-model="resumedPage"
      class="my-4"
      :length="Math.ceil(resumed.total / resumedItemsPerPage)"
      @input="loadResumed"
      :total-visible="7"
    ></v-pagination>
  </div>
</template>

<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import { Globals } from '../../../../modules/Globals';
  import { IPaginated } from '../../../../interfaces/IPaginated';
  import { IExpiringRestaurant } from '../../../../interfaces/IExpiringRestaurant';
  import RestaurantsService from '../../../../api/services/restaurants.service';
  import { vueData } from '../../../../VueData';
  import { IRestaurant } from '../../../../interfaces/IRestaurant';
  import { debounce } from '../../../../utils';
  import { IUser } from '../../../../interfaces/IUser';
  import SelectModal from '../../../modals/SelectModal.vue';
  import { AlertType } from '../../../../interfaces/IAlert';
  import DoubleInputModal from '../../../modals/DoubleInputModal.vue';
  import DoubleSelectModal from '../../../modals/DoubleSelectModal.vue';
  import UploadMenuModal from '../../../modals/UploadMenuModal.vue';
  import { getTopLevelDomain } from '@/utils/domain.utils';
  import { getEffectiveTier, isPlanExpired } from '@/utils/billing-entitlements.utils';

  @Component
  export default class RestaurantList extends Vue {
    @Prop() user: IUser;

    restaurantService = new RestaurantsService();
    uploadsPath = Globals.uploadsPath;

    resumed: IPaginated<IExpiringRestaurant> = null;
    loadingResumed: boolean = true;
    resumedPage: number = 1;
    resumedFilter: string = null;
    resumedItemsPerPage: number = 10;
    resumedOrderTypes = [
      { id: 'id', name: 'Fecha de creación' },
      { id: 'planExpiresAt', name: 'Vencimiento del plan' },
      { id: 'paidMonths', name: 'Meses pagos' },
      { id: 'ordersLastWeek', name: 'Pedidos última semana' },
      { id: 'lastPaymentDate', name: 'Fecha último pago' },
    ];
    selectedOrderType: any = null;
    imgSize = 50;
    domain = getTopLevelDomain();
    getEffectiveTier = getEffectiveTier;
    isPlanExpired = isPlanExpired;

    headers = [
      { text: '', value: 'picture', width: 50 },
      { text: 'Tienda', value: 'id' },
      { text: 'Contacto', value: 'contact' },
      { text: 'Creación', value: 'createdAt' },
      { text: 'Meses pagos', value: 'paidMonths' },
      { text: 'Último pago', value: 'lastPaymentDate' },
      { text: '', value: 'actions' },
    ];

    filters = {
      onlyPaid: false,
      hideLite: false,
      onlyHaveOrders: false,
    };

    delayedReload: () => void = null;

    whatsappMessage =
      'Hola! Soy Alejandro de QueResto.com. Me comunico para avisar que su plan en la plataforma está próximo a vencer. Si quiere seguir recibiendo pedidos, no se olvide de renovarlo!\r\n\r\n';

    async created() {
      this.selectedOrderType = this.resumedOrderTypes[0];
      this.loadResumed();

      this.delayedReload = debounce(() => {
        this.resumedPage = 1;
        this.loadResumed();
      }, 250);
    }

    async loadResumed() {
      this.loadingResumed = true;
      try {
        const resumed = await this.restaurantService.getResumedRestaurants({
          page: this.resumedPage,
          search: this.resumedFilter,
          order: this.selectedOrderType.id,
          onlyPaid: this.filters.onlyPaid,
          onlyHaveOrders: this.filters.onlyHaveOrders,
          hideLite: this.filters.hideLite,
          itemsPerPage: this.resumedItemsPerPage,
        });
        this.resumed = resumed;
      } catch (e) {
        vueData.serverError(e);
      } finally {
        this.loadingResumed = false;
      }
    }

    showUploadMenu(restaurant: IRestaurant) {
      this.$modal.show(
        UploadMenuModal,
        {
          restaurant,
        },
        {
          name: 'upload-menu',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
          clickToClose: false,
        },
        {
          submit: async () => {},
        }
      );
    }

    showAddPlanDialog(restaurant: IExpiringRestaurant) {
      this.$modal.show(
        DoubleSelectModal,
        {
          title: 'Elija duración',
          message:
            'La cantidad de meses se sumará al período restante, si es que tiene',
          placeholder: 'Seleccione el plan',
          placeholder2: 'Seleccione una duración',
          items: [
            { value: 1, text: 'Plus' },
            { value: 2, text: 'Pro' },
          ],
          items2: [
            { value: 1, text: 'Un mes' },
            { value: 3, text: 'Tres meses' },
            { value: 6, text: 'Seis meses' },
            { value: 12, text: 'Doce meses' },
          ],
        },
        {
          name: 'add-plan',
          adaptive: true,
          height: 'auto',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          width: 600,
          clickToClose: false,
        },
        {
          submit: async (values: [number, number]) => {
            try {
              await this.restaurantService.addPlan(
                restaurant.id,
                values[0],
                values[1]
              );
              await this.loadResumed();
              vueData.addAlert('Plan aumentado con éxito', AlertType.Success);
            } catch (e) {
              console.error(e);
              vueData.serverError(e);
            }
            this.$modal.hide('add-plan');
          },
        }
      );
    }

    @Watch('resumedFilter')
    onResumedFilterChanged() {
      this.delayedReload();
    }

    @Watch('filters', { deep: true })
    onFiltersChanged() {
      if (this.selectedOrderType.id === 'planExpiresAt')
        this.filters.hideLite = true;

      this.resumedPage = 1;
      this.loadResumed();
    }

    @Watch('selectedOrderType')
    onOrderChanged(type) {
      if (type.id === 'planExpiresAt') this.filters.hideLite = true;

      this.resumedPage = 1;
      this.loadResumed();
    }

    getWhatsappMessage(r: IRestaurant) {
      return encodeURIComponent(
        this.whatsappMessage +
          `Puede renovar el plan desde aquí https://${this.domain}/admin/${r.stringId}/settings?tab=plan`
      );
    }
  }
</script>
